import { z } from 'zod';
import { MAX_ARTIFACT_BYTES } from './builds.js';
import { Sha256, SigningIdentity, Ulid } from './common.js';
import { ReportNamedBy } from './feedback-ask.js';
import { PlayLayerConfig } from './play.js';
import { PLAYGROUND_CHANNEL_SLUG, PUBLIC_CHANNEL_SLUG } from './store.js';
/**
 * The in-game feedback layer (plan §18, `docs/feedback-plan.md`).
 *
 * Everything about the button's geometry, its defaults and what a report is lives here, because
 * three separate renderers have to agree on it: the dashboard preview (React), the injected
 * Android layer (Java) and the injected web layer (vanilla JS). The Java and JS constants are
 * **generated from this module at patch time**, not hand-copied — if you find yourself typing
 * `64` into a `.java` file, stop and fix the generator.
 */
/* ---------------------------------------------------------------------------- what a report is */
/**
 * What the player said this is.
 *
 * Two values, fixed. It is not a taxonomy a developer extends — a ticket's type and tags are for
 * that (`tickets.ts`) — and it is not inferred from the text. The modal asks, and the layer always sends an answer. Adding a
 * third value later means re-patching every build already on a Board, so it is worth being sure.
 */
export const FeedbackKind = z.enum(['bug', 'feature']);
export const MAX_FEEDBACK_MESSAGE_CHARS = 4000;
export const MAX_FEEDBACK_SCREENSHOT_BYTES = 2 * 1024 * 1024;
/**
 * How long a screenshot is kept (docs/feedback-plan.md §7.1).
 *
 * The *report* is kept as long as the game is: the text is what a team goes back to. The picture
 * is a photograph of somebody's screen, taken in their home, and it is not something to keep
 * forever because nobody wrote down when it should go. After this the object is deleted and the
 * row says `screenshot_status: 'none'`, which the detail panel explains rather than showing a
 * broken image.
 */
export const FEEDBACK_SCREENSHOT_RETENTION_DAYS = 180;
/**
 * Where a report's screenshot is.
 *
 * `none` and `expired` are different answers and the difference is the whole reason `expired`
 * exists: one means the player did not attach a picture, the other means they did and the
 * retention window (`FEEDBACK_SCREENSHOT_RETENTION_DAYS`) has since taken it. Collapsing them
 * would leave the detail panel unable to tell a reviewer which it is looking at.
 */
export const FeedbackScreenshotStatus = z.enum([
    'none',
    'uploading',
    'ready',
    'failed',
    'expired',
]);
/* ------------------------------------------------------------------------------- ingest */
/**
 * What the injected layer posts to `POST /v1/feedback` (docs/feedback-plan.md §7.3).
 *
 * **This shape is already shipping.** `FeedbackSubmitter` in `patcher/android-layer` was built in
 * B2a and every build patched since then sends exactly these fields, so this schema is a
 * description of an existing wire format rather than a proposal. A field cannot be renamed here
 * without re-patching every build already on a Board.
 *
 * Everything below `kind` is *declared*, exactly as a build's version name is: the device model
 * and the OS version are what the reporting process said about itself, they are stored verbatim,
 * and they are trusted for nothing. A report from an app that could not describe itself is still
 * a report, which is why all of it is optional.
 */
export const SubmitFeedbackRequest = z.object({
    /** The game's ingest key, baked into the build. An identifier, not a credential — see §5.3. */
    key: z.string().min(1).max(200),
    buildId: Ulid,
    /** Always sent. There is no default here on purpose: a guessed `kind` is a `kind` that stops
     *  meaning anything, and the modal always asks. */
    kind: FeedbackKind,
    message: z.string().trim().min(1).max(MAX_FEEDBACK_MESSAGE_CHARS),
    hasScreenshot: z.boolean().default(false),
    deviceModel: z.string().max(200).nullish(),
    osVersion: z.string().max(200).nullish(),
    appVersionName: z.string().max(200).nullish(),
    appVersionCode: z.number().int().nullish(),
    locale: z.string().max(40).nullish(),
});
/**
 * The answer, and the contract the shipped Android layer reads: it takes `uploadUrl` and
 * `finalizeUrl` off this object and, finding either missing, sends the report without a picture
 * rather than failing. Both are null when the player attached nothing, and when the game's
 * `allowScreenshot` is off.
 */
export const SubmitFeedbackResponse = z.object({
    id: Ulid,
    uploadUrl: z.string().nullable(),
    finalizeUrl: z.string().nullable(),
    expiresAt: z.number().int().nullable(),
});
/* ------------------------------------------------------------------------------- reports */
/** The build a report came from, as far as the dashboard needs to name it. */
export const FeedbackReportBuild = z.object({
    id: Ulid,
    versionName: z.string(),
    versionCode: z.number().int(),
    buildNumber: z.number().int().nullable(),
    channelSlug: z.string(),
});
/**
 * Which channel a report came from.
 *
 * Recorded on the report itself rather than read back through its build, because the button is a
 * per-channel decision and "what are the Beta testers saying" has to keep working after the build
 * they said it about has been pruned. Null only when the channel itself was deleted.
 */
export const FeedbackReportChannel = z.object({
    id: Ulid,
    name: z.string(),
    slug: z.string(),
});
export const FeedbackReport = z.object({
    id: Ulid,
    appId: Ulid,
    channel: FeedbackReportChannel.nullable(),
    kind: FeedbackKind,
    message: z.string(),
    /**
     * Who reported it, or null for anonymous — which is every report until §6 ships, and plenty
     * of them afterwards. Attribution never blocks a submission.
     *
     * A real name when the reporter is in the team reading this, `@handle` otherwise. A tester who
     * files a bug from inside a game has not agreed to introduce themselves to the studio.
     */
    reporterName: z.string().nullable(),
    /**
     * How the report came by `reporterName`: a signed-in phone (`session`), or the account that
     * paired the Board it was typed on (`board`), whose name then reads as *@zebra's Board* —
     * whose Board, never who typed (`docs/feedback-plan.md` §6.3). Null whenever the name is.
     */
    namedBy: ReportNamedBy.nullable(),
    /** Null when the build has since been deleted. The report outlives it. */
    build: FeedbackReportBuild.nullable(),
    deviceModel: z.string().nullable(),
    osVersion: z.string().nullable(),
    appVersionName: z.string().nullable(),
    appVersionCode: z.number().int().nullable(),
    locale: z.string().nullable(),
    screenshotStatus: FeedbackScreenshotStatus,
    createdAt: z.number().int(),
});
export const FeedbackReportPage = z.object({
    items: z.array(FeedbackReport),
    nextCursor: z.string().nullable(),
    /** How many reports match the filters, on the first page only (no `cursor`). */
    total: z.number().int().optional(),
});
/* -------------------------------------------------------------------------------- the button */
export const FeedbackButtonSize = z.enum(['small', 'medium', 'large']);
/**
 * Four corners and four edge centres, and two places tucked against the Board's own menu button
 * (`BESIDE_MENU_POSITIONS`). There is no centre: it would sit on the game.
 */
export const FeedbackButtonPosition = z.enum([
    'top-left',
    'top-center',
    'top-right',
    'right-center',
    'bottom-right',
    'bottom-center',
    'bottom-left',
    'left-center',
    'top-right-menu-left',
    'top-right-menu-below',
]);
/**
 * The two positions beside the Board's menu button: to its left, and under it.
 *
 * Spelled `top-right-…` on purpose. The web layer from before these existed reads a position as
 * its first two words, so a build patched before them that is told one of these by the config
 * endpoint draws in the top-right corner rather than in the middle of the game; an older Android
 * layer does not know the word and falls back to bottom-right. A re-patch puts either right.
 */
export const BESIDE_MENU_POSITIONS = ['top-right-menu-left', 'top-right-menu-below'];
export function isBesideMenu(position) {
    return BESIDE_MENU_POSITIONS.includes(position);
}
/**
 * The menu button BoardOS draws over the top-right of every game, in design dp against the 960 dp
 * console both layers scale from.
 *
 * Measured, not documented — Board publishes none of it. On a 1920 × 1080 capture of a web game on
 * a Board (`apps/web/public/marketing/feedback.webp`) it is an 80 px square, 16 px below the top
 * edge and 12 px in from the right, with corners of about 24 px. If a Board OS moves it, these
 * move, and nothing else does.
 */
export const BOARD_MENU_BUTTON_DP = { size: 40, top: 8, right: 6, radius: 12 };
/** The gap between the menu button and a feedback button beside it: as tight as its own inset. */
export const BESIDE_MENU_GAP_DP = 6;
/**
 * Where a feedback button beside the menu button sits, as its distance from the top and right
 * edges in design dp. Its size and its corners are the menu button's own, whatever size the
 * channel picked, so the two read as one pair rather than as a big button crowding a small one.
 */
export const BESIDE_MENU_DP = {
    'top-right-menu-left': {
        top: BOARD_MENU_BUTTON_DP.top,
        right: BOARD_MENU_BUTTON_DP.right + BOARD_MENU_BUTTON_DP.size + BESIDE_MENU_GAP_DP,
    },
    'top-right-menu-below': {
        top: BOARD_MENU_BUTTON_DP.top + BOARD_MENU_BUTTON_DP.size + BESIDE_MENU_GAP_DP,
        right: BOARD_MENU_BUTTON_DP.right,
    },
};
/** Design dp, against the 960 dp console width the injected overlay already scales from. */
export const FEEDBACK_BUTTON_DP = {
    small: 48,
    medium: 64,
    large: 80,
};
/**
 * The gap between the button and the screen edge. Derived from size, not a separate knob — a
 * large button hard against the bezel reads as a rendering bug, and one more slider on this
 * screen buys nothing.
 */
export const FEEDBACK_BUTTON_MARGIN_DP = {
    small: 12,
    medium: 16,
    large: 20,
};
/**
 * `#RRGGBB`, or `#RRGGBBAA` where a colour needs to be partly transparent on its own — the
 * default border is a faint ink line, which is an alpha, not a lighter grey. Uppercased so two
 * spellings of the same colour compare equal, in D1 and in a `BugConfig.java` diff alike.
 */
export const HexColor = z
    .string()
    .transform((s) => s.trim().toUpperCase())
    .refine((s) => /^#[0-9A-F]{6}([0-9A-F]{2})?$/.test(s), {
    message: 'Must be a hex colour such as "#FFFEF1" or "#0000001F".',
});
/**
 * What a stored row puts in `border_color` when the developer removed the border.
 *
 * The border is the one field where "nobody has chosen" and "chosen to be nothing" are both real
 * and both used to be spelled `NULL`, which meant the panel's *Remove* button wrote a value that
 * resolved straight back to the default border. One of the two needs a name, and a border is
 * more honestly described by the absence of a colour than by a transparent one — a transparent
 * border still costs a pixel of layout on some renderers, and the injected layers already carry
 * a "no border" branch rather than drawing `#00000000`.
 */
export const NO_BORDER = 'none';
/** A colour, `NO_BORDER`, or null for "nobody has chosen, so the default applies". */
export const StoredBorderColor = z.union([z.literal(NO_BORDER), HexColor]).nullable();
export const FeedbackLayerAppearance = z.object({
    backgroundColor: HexColor,
    backgroundOpacity: z.number().min(0).max(1),
    iconColor: HexColor,
    /** null = no border at all, which is a real choice and not "transparent". */
    borderColor: HexColor.nullable(),
    size: FeedbackButtonSize,
    position: FeedbackButtonPosition,
});
/**
 * What a game gets before anyone touches a colour picker.
 *
 * `bottom-right` deliberately: the Board exit overlay (`scripts/patch-board.sh`, `BoardOverlay`)
 * defaults to the *top-right* corner, and two floating buttons stacking in one corner is the
 * obvious failure. That overlay only reaches an APK someone ran `patch-board.sh` over, so the
 * collision is possible rather than usual — but the dashboard preview draws the exit button's
 * default position greyed anyway, so a developer who picks top-right is choosing it knowingly.
 */
export const FEEDBACK_LAYER_DEFAULTS = {
    backgroundColor: '#FFFEF1', // ivory, matching the exit overlay
    backgroundOpacity: 0.92,
    iconColor: '#000000',
    borderColor: '#0000001F',
    size: 'medium',
    position: 'bottom-right',
};
/* ------------------------------------------------------------------------------- settings */
/**
 * One channel's settings, exactly as they are stored.
 *
 * **The channel owns the button.** There is no game-level row above this one and nothing to
 * inherit from: a developer decides per channel whether their playtesters get a button and what
 * it looks like, because a channel is an audience and two audiences are not variations of one
 * setting. Migration 0018 is where the old game row was folded into its channels.
 *
 * `enabled` and `allowScreenshot` are stated. The appearance fields are nullable, and there null
 * means *no choice has been made* — the value comes from `FEEDBACK_LAYER_DEFAULTS`, so a channel
 * that never picked a colour follows the default if the default ever moves. It does not mean
 * "read another row"; there is no other row.
 */
export const StoredFeedbackLayer = z.object({
    enabled: z.boolean(),
    allowScreenshot: z.boolean(),
    backgroundColor: HexColor.nullable(),
    backgroundOpacity: z.number().min(0).max(1).nullable(),
    iconColor: HexColor.nullable(),
    borderColor: StoredBorderColor,
    size: FeedbackButtonSize.nullable(),
    position: FeedbackButtonPosition.nullable(),
});
/**
 * A settings write, against one channel.
 *
 * Every field is optional and absent means "leave it". Nothing here is nullable except
 * `borderColor`, where null is a real choice — no border at all — rather than the absence of one.
 */
export const UpdateFeedbackLayerRequest = z
    .object({
    /** Required. There is no game-level row to target. */
    channelId: Ulid,
    enabled: z.boolean().optional(),
    allowScreenshot: z.boolean().optional(),
    backgroundColor: HexColor.optional(),
    backgroundOpacity: z.number().min(0).max(1).optional(),
    iconColor: HexColor.optional(),
    /** null = no border, which is a choice and not "transparent". */
    borderColor: HexColor.nullable().optional(),
    size: FeedbackButtonSize.optional(),
    position: FeedbackButtonPosition.optional(),
})
    .refine((v) => Object.keys(v).length > 1, 'No fields to update.');
/**
 * What the layer running inside a game is told, and what the dashboard preview draws. Resolved,
 * with nothing left to inherit.
 */
export const FeedbackLayerConfig = z.object({
    enabled: z.boolean(),
    allowScreenshot: z.boolean(),
    appearance: FeedbackLayerAppearance,
});
/* --------------------------------------------------------------------------- the update notice */
/**
 * How often a running game asks whether a newer build has landed on its channel.
 *
 * A minute because the channel this is on by default is `playground`, where the person waiting for
 * the notice is the developer who just pressed Publish; anything slower reads as "it did not work".
 * The ask is the layer's ordinary config read, answered with a 304 almost every time, and only
 * while the game is in front — a Board in standby asks nothing. Generated into both injected
 * layers from here, like every other number they share.
 */
export const UPDATE_CHECK_SECONDS = 60;
/**
 * The build a running game is told about: what a tester's BoardFlight will offer them as the
 * update, named the way the client's own update popup names it.
 */
export const UpdateNoticeBuild = z.object({
    id: Ulid,
    versionName: z.string(),
    versionCode: z.number().int(),
});
/**
 * At most this many builds' notes ride in one answer, newest first. A player more than ten builds
 * behind is told what the last ten changed; the rest is in the library, where it always was.
 */
export const UPDATE_CHANGES_MAX = 10;
/** One build's notes are cut to this many characters before they go to a game. */
export const UPDATE_CHANGE_NOTES_MAX = 1_500;
/**
 * One build a player gets by updating, and what its uploader said it changed: the release notes
 * BoardFlight already shows testers in the library and the dashboard, which the playground writes
 * at every publish. A game draws them as its changelog before it hands the player to the update
 * card (`board-updates` skill). `notes` is `''` for a build published without any.
 */
export const UpdateNoticeChange = z.object({
    versionName: z.string(),
    versionCode: z.number().int(),
    notes: z.string(),
});
/**
 * The update notice's half of the config answer.
 *
 * `enabled` is the channel's switch as it stands now — **whether the layer draws its card by
 * itself** — so turning the notice off reaches a build already on Boards the same way the feedback
 * button's kill switch does.
 *
 * `latest` is the newer build the tester's library would actually offer, or null. It is computed
 * for every build the server can identify, whatever `enabled` says (off-board-plan §7.1): a game
 * that asks about its own version through `@boardflight/game` gets the same answer the card would
 * have drawn, and decides for itself what to do with it. A layer that finds a build here can say
 * "there is an update" without knowing any of the rules that decide it.
 *
 * `url` is the page where a person updates — the game's share page when it has one, its library
 * page otherwise — and what the card's QR code encodes. Absent for a build the server cannot
 * identify, whose answer is exactly what it was before any of this existed.
 *
 * `changes` is every build between this one and `latest`, `latest` included and newest first, by
 * the same rules that pick `latest` — at most `UPDATE_CHANGES_MAX`, each build's notes cut to
 * `UPDATE_CHANGE_NOTES_MAX`. Present only beside a `latest`, so a build that is up to date is
 * answered byte for byte as before and keeps its ETag. Only what BoardFlight knows about the
 * calling game, on the reply path it already has.
 */
export const UpdateNoticeConfig = z.object({
    enabled: z.boolean(),
    latest: UpdateNoticeBuild.nullable(),
    url: z.string().optional(),
    changes: z.array(UpdateNoticeChange).max(UPDATE_CHANGES_MAX).optional(),
});
/**
 * The build that is asking, as the server knows it (off-board-plan §7.1).
 *
 * Nothing about a build's version is baked into the layer — the version name and code are the
 * uploader's word, recorded at upload — so a game that wants to print "v0.0.14" in a corner asks
 * the layer, and the layer reads it from here. `channel` is the slug of the channel the build sits
 * on **today**, which a promotion can change and a baked value could not follow.
 */
export const LayerBuildInfo = z.object({
    versionName: z.string(),
    versionCode: z.number().int(),
    channel: z.string(),
});
/**
 * Whether a channel shows the in-game update notice before anybody has said.
 *
 * On for a web game's `playground` and `public` channels and off everywhere else, and this is the
 * only place that rule is written: whatever creates a channel asks it, migration 0042 applied it
 * once to the `playground` channels that already existed, and migration 0069 to the `public` ones.
 * After that the column is the channel's own answer and this is never consulted again.
 *
 * `playground`, because it is the one channel whose audience is the developer: a build pressed out
 * of the playground is meant to be on the Board in the next minute, and a notice is the difference
 * between knowing it arrived and relaunching to find out. `public` (off-board-plan P3), because
 * nothing of BoardFlight's runs on a Board any more: the card is the only thing that reaches a
 * player whose desktop app is closed, and a public web build is patched anyway (`sessionsDefault`).
 *
 * **Web only**, because a web build is patched without being re-signed. On a Unity game the same
 * default would change the signature of every build on that channel and cost everybody holding one
 * an uninstall — a decision a team has to make on purpose, with the dialog that says so, not one a
 * default makes for them.
 */
export function updateNoticeDefault(slug, platform) {
    return platform === 'web' && (slug === PLAYGROUND_CHANNEL_SLUG || slug === PUBLIC_CHANNEL_SLUG);
}
/**
 * Whether a channel reports play sessions before anybody has said (off-board-plan decision 12).
 *
 * On for a web game's `public` channel and off everywhere else. The library ranks on plays, a play
 * is a session the game itself reports (`docs/store-plan.md` §5), and a ranking has to measure
 * every game the same way — so the session ping is in every public web build unless its team takes
 * it out. Sessions ride on the channel's analytics switch (`resolveAnalytics`), which `public` has
 * had on from creation; a team that switched that off has switched this off with it.
 *
 * Written down the way `updateNoticeDefault` is: whatever creates a `public` channel asks it,
 * migration 0069 applied it once to the ones that already existed and had not decided, and after
 * that `analytics_settings.sessions_enabled` is the channel's own answer.
 *
 * **Web only, for the reason above and one more:** a Unity build is never patched by a default. A
 * Unity game's public channel stays opt-in and ranks on installs until its team turns the layer on.
 *
 * It is also what makes play sessions a *soft* reason to patch on that one channel
 * (`shouldPatchBuild`): a default nobody chose asks for a patch only where the deployment has a
 * patcher.
 */
export function sessionsDefault(slug, platform) {
    return platform === 'web' && slug === PUBLIC_CHANNEL_SLUG;
}
/**
 * What `GET /v1/feedback-layer/config` answers: the resolved layer, the analytics half of the
 * same injected DEX, and enough to name the game.
 *
 * `game.name` is here for the phone form (§4.5), which is opened by scanning a code off a
 * Board's screen and has to say *which game* it is about before it asks anybody to type. It is not
 * new information: whoever can call this holds the ingest key, which means they hold a copy of
 * the build, which carries the game's own name and package id. The endpoint's other fields have
 * always been readable the same way.
 *
 * Both injected layers ignore keys they have never heard of — `FeedbackConfig.apply` parses with
 * `opt*` — so a field added here reaches an old build harmlessly.
 */
export const PublicFeedbackLayerConfig = FeedbackLayerConfig.extend({
    game: z.object({ name: z.string() }),
    analytics: z.object({
        enabled: z.boolean(),
        heartbeatSeconds: z.number().int(),
    }),
    /**
     * Custom game events (docs/game-events-plan.md §8.3): the kill switch for builds already on
     * Boards. The route has answered with it since events shipped; it is declared here so the type
     * says what the wire does.
     */
    events: z.object({ enabled: z.boolean() }),
    /**
     * Achievements and leaderboards (docs/achievements-plan.md §8.4): each service's kill switch for
     * builds already on Boards, and the catalog version the layer's copy should match.
     *
     * **Absent means both off**, and it is absent whenever both are — so a build this server cannot
     * place, and every channel without play services, gets the answer byte for byte as before, and an
     * old layer's cached ETag still matches.
     */
    play: PlayLayerConfig.optional(),
    /** The third subsystem in the same injected layer. See `UpdateNoticeConfig`. */
    update: UpdateNoticeConfig,
    /** The caller's own build. Absent for a build the server cannot identify. See `LayerBuildInfo`. */
    build: LayerBuildInfo.optional(),
    /**
     * Where this game's printed figures are sold (docs/meshy-models-architecture.md §11): its own
     * library page, at the shelf. What a game asking `{ q: 'figures' }` is answered with, and a game
     * shows as a QR code or a line.
     *
     * **Absent unless there is something to buy** — the shop open, the game one of Boardaux's own, in
     * the library, with a set listed and priced — so every other game's answer is byte for byte what
     * it was, and its ETag still matches.
     */
    figures: z.object({ url: z.string() }).optional(),
});
/** One channel's settings as the dashboard sees them: resolved, with nothing left to fill in. */
export const FeedbackChannelLayer = z.object({
    channelId: Ulid,
    channelName: z.string(),
    channelSlug: z.string(),
    config: FeedbackLayerConfig,
    /**
     * Whether anyone has ever saved settings for this channel.
     *
     * Not the same as `config.enabled === false`: a channel nobody has touched and a channel
     * somebody deliberately turned the button off on look identical in `config`, and the settings
     * screen says something different about each.
     */
    configured: z.boolean(),
    updatedAt: z.number().int().nullable(),
});
export const FeedbackLayerSettings = z.object({
    /** Every channel of the game, in channel order, whether or not it has been configured. */
    channels: z.array(FeedbackChannelLayer),
});
/* ------------------------------------------------------------------- the signing identity */
/**
 * A certificate fingerprint as lowercase hex.
 *
 * `keytool -list` prints it colon-separated and uppercase (`A1:B2:…`) and Android's
 * `PackageManager` hands back raw bytes, so both spellings arrive and neither is wrong. They are
 * folded to one here, because the whole use of this value is comparing two of them — the client's
 * pre-download check (§3.4) asks whether the certificate on the device matches the one the server
 * says the build carries, and two spellings would answer "no" to two identical keys.
 */
export const CertSha256 = z
    .string()
    .transform((s) => s.trim().replace(/:/g, '').toLowerCase())
    .refine((s) => /^[0-9a-f]{64}$/.test(s), 'Must be a SHA-256 certificate fingerprint.');
/**
 * The key a game's patched builds are signed with, as the dashboard sees it.
 *
 * There is no password here and never will be — a store password is wrapped before it reaches
 * D1 and is unwrapped only into a job file. What a developer needs to *see* is which certificate
 * they are looking at, which is why `certSha256` is on this and the passwords are not.
 */
export const AppSigningKey = z.object({
    origin: z.enum(['boardflight', 'team']),
    keyAlias: z.string(),
    /**
     * Null until the first patch job runs. It is read in the sandbox with `keytool`, never in the
     * Worker — that is the artifact rule (`CLAUDE.md`) applied to a keystore, and it means an
     * upload that is not a keystore at all fails a patch job with `keytool`'s own message rather
     * than being parsed by something in this repo.
     */
    certSha256: z.string().nullable(),
    uploadedBy: z.string().nullable(),
    createdAt: z.number().int(),
});
/** Everything the settings panel needs to tell the truth about turning the layer on. */
export const SigningKeyStatus = z.object({
    key: AppSigningKey.nullable(),
    /**
     * How many testers have this game installed right now, as far as `install_events` knows.
     *
     * On the panel this is the size of the "everyone has to uninstall first" cost, and it is the
     * difference between a warning a developer can act on and one they scroll past. Zero is a real
     * and common answer, and the dialog says something different for it.
     */
    installedTesters: z.number().int(),
    /** Web builds are not signed at all, so for a web game none of this applies. */
    signingApplies: z.boolean(),
});
/**
 * Registering a keystore the team uploaded.
 *
 * The passwords are sent once, wrapped on arrival and never returned. There is no way to read
 * one back out of BoardFlight, which is deliberate: the only thing that ever needs it is a patch
 * job, and it gets it in a job file that lives for the length of one sandbox.
 */
export const RegisterSigningKeyRequest = z.object({
    keyAlias: z.string().trim().min(1).max(200),
    storePassword: z.string().min(1).max(400),
    /** Omit when it is the same as the store password, which is the common case. */
    keyPassword: z.string().min(1).max(400).nullable().optional(),
});
/* ---------------------------------------------------------------------------- patch jobs */
/**
 * What the patch runner reports back (`POST /v1/patch-jobs/:id/result`, §3.6).
 *
 * `sha256` and `size` are required on success and are not advisory: the row still carries the
 * developer's digest at that moment, and `CLAUDE.md` says a build's `sha256` is verified on the
 * device. A result that omitted them, or that the server declined to apply, would publish
 * patched bytes under an unpatched hash and fail every install on every Board.
 *
 * The two signing fields are optional *here* and required by the route for any platform that
 * signs. A `.webapp.zip` has no signature at all — the web patcher adds two files and a script
 * tag and never opens a keystore — so a runner reporting one would be claiming something untrue
 * about bytes nobody signed. Which platforms sign is a fact about the build, which the server
 * knows and the runner does not have to be trusted about, so the check lives there.
 */
export const PatchJobResultRequest = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        sha256: Sha256,
        size: z.number().int().min(1).max(MAX_ARTIFACT_BYTES),
        /** The certificate the runner actually signed with, read back out of the keystore. */
        certSha256: CertSha256.optional(),
        signingIdentity: z.enum(['boardflight', 'team']).optional(),
        log: z.string().max(20_000).optional(),
    }),
    z.object({
        ok: z.literal(false),
        error: z.string().max(4_000),
        log: z.string().max(20_000).optional(),
    }),
]);
/** What a developer can see about a patch attempt on their own build. */
export const PatchJobSummary = z.object({
    id: Ulid,
    buildId: Ulid,
    status: z.enum(['queued', 'running', 'done', 'failed']),
    attempts: z.number().int(),
    signingIdentity: SigningIdentity.nullable(),
    error: z.string().nullable(),
    createdAt: z.number().int(),
    finishedAt: z.number().int().nullable(),
});
/**
 * One channel's stored settings, with every unmade choice filled in from the defaults.
 *
 * This function is the only place that filling-in is written down, and the API, the dashboard
 * preview, the patch job file and the public config endpoint all call it. Two implementations of
 * it would be two answers to "what colour is the button", one of which would be wrong on
 * someone's Board.
 *
 * `null` — a channel with no settings at all — is a channel whose button is off. That is the
 * answer for every channel of every game that has never touched this feature, and it is a real
 * answer rather than a missing one.
 */
export function resolveFeedbackLayer(channel) {
    return {
        enabled: channel?.enabled ?? false,
        allowScreenshot: channel?.allowScreenshot ?? true,
        appearance: {
            backgroundColor: channel?.backgroundColor ?? FEEDBACK_LAYER_DEFAULTS.backgroundColor,
            backgroundOpacity: channel?.backgroundOpacity ?? FEEDBACK_LAYER_DEFAULTS.backgroundOpacity,
            iconColor: channel?.iconColor ?? FEEDBACK_LAYER_DEFAULTS.iconColor,
            // Three states, not two: a colour, `NO_BORDER` because somebody removed it, and null
            // because nobody has said. Only the last one takes the default.
            borderColor: channel?.borderColor === NO_BORDER
                ? null
                : (channel?.borderColor ?? FEEDBACK_LAYER_DEFAULTS.borderColor),
            size: channel?.size ?? FEEDBACK_LAYER_DEFAULTS.size,
            position: channel?.position ?? FEEDBACK_LAYER_DEFAULTS.position,
        },
    };
}
//# sourceMappingURL=feedback.js.map